import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useMatchRoute } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { type NavItem } from '@/hooks/use-app-config';
import { cn } from '@/lib/utils';

interface NavMainTopbarItemProps {
  item: NavItem;
  className?: string;
}

export const NavMainTopbarItem: FC<NavMainTopbarItemProps> = ({
  item,
  className,
}: NavMainTopbarItemProps) => {
  const { t } = useTranslation();
  const matchRoute = useMatchRoute();

  const isRouteActive = !!matchRoute({ to: item.location.href, fuzzy: true });
  const isChildRouteActive = !!item.items?.some((subItem) =>
    matchRoute({ to: subItem.location.href, fuzzy: false })
  );

  const isActive = isRouteActive || isChildRouteActive;

  if (item.items?.length && item.items.length > 0) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className={cn(
              isActive && 'bg-accent text-accent-foreground font-semibold'
            )}>
            {item.title}
            <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='center'>
          {item.items.map((subItem) => (
            <DropdownMenuItem
              key={subItem.title}
              asChild>
              <Link
                to={subItem.location.href}
                className={cn(
                  matchRoute({ to: subItem.location.href, fuzzy: false }) &&
                    'bg-accent text-accent-foreground font-semibold'
                )}>
                {subItem.title}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const Icon = item.icon;
  return (
    <Link
      data-testid={item.testId ? `topbar-tab-${item.testId}` : undefined}
      to={item.location.href}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 flex h-full items-center gap-2 border-y-[2.5px] border-transparent px-3 text-sm outline-none focus-visible:ring-[3px]',
        isActive && 'border-b-brand text-foreground font-semibold',
        className
      )}>
      {Icon && <Icon className={cn('size-4', isActive && 'text-brand')} />}
      {item.title}
      {!!item.count && (
        <span
          className='bg-brand/15 text-foreground rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums'
          aria-label={t('common.waitingCount', { count: item.count })}>
          {item.count}
        </span>
      )}
    </Link>
  );
};
